

import React, { useState, useEffect } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/Logo';

// New type for API status
type ApiStatus = 'checking' | 'online' | 'offline';

// New component for the status indicator
const ApiStatusIndicator: React.FC<{ status: ApiStatus; version: string | null }> = ({ status, version }) => {
  const statusConfig = {
    checking: { text: 'Verificando conexão com o servidor...', color: 'bg-gray-400', textColor: 'text-gray-600 dark:text-gray-400' },
    online: { text: 'Servidor Online', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-400' },
    offline: { text: 'Servidor Offline', color: 'bg-red-500', textColor: 'text-red-700 dark:text-red-400' },
  };
  
  const config = statusConfig[status];
  const pulseClass = status === 'checking' ? 'animate-pulse' : '';

  return (
    <div className="flex flex-col items-center justify-center space-y-1 text-sm">
      <div className="flex items-center space-x-2">
        <span className={`h-2.5 w-2.5 rounded-full ${config.color} ${pulseClass}`}></span>
        <span className={config.textColor}>{config.text}</span>
      </div>
      {version && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Versão do Servidor: {version}
        </span>
      )}
    </div>
  );
};

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();
  const navigate = ReactRouterDom.useNavigate();
  
  // New state for API status
  const [apiStatus, setApiStatus] = useState<ApiStatus>('checking');
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  // useEffect to check API health on mount
  useEffect(() => {
    const checkApiStatus = async () => {
      setServerVersion(null);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 seconds timeout
        
        const response = await fetch('/api/diagnostics', { signal: controller.signal });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setApiStatus('online');
            if (data.version) {
              setServerVersion(data.version);
            }
            return;
          }
        }
        setApiStatus('offline');
      } catch (err) {
        console.error('API health check failed:', err);
        setApiStatus('offline');
      }
    };
    checkApiStatus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await auth.login(email, password);
      if (success) {
        // Always navigate to dashboard on successful login, ignoring previous location.
        navigate('/dashboard', { replace: true });
      } else {
        setError('E-mail ou senha inválidos.');
      }
    } catch (err) {
      setError('Falha ao tentar fazer login. Tente novamente.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const isFormDisabled = apiStatus !== 'online' || isLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4">
        <div>
          <Logo companyName="Lam Custos" isSidebarOpen={true} className="mx-auto justify-center mb-6" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-dark-text-primary">
            Acesse sua conta
          </h2>
        </div>
        
        <div className="text-center py-2">
            <ApiStatusIndicator status={apiStatus} version={serverVersion} />
            {apiStatus === 'offline' && (
                <div className="mt-3 text-xs text-red-600 dark:text-red-400 bg-red-500/10 p-3 rounded-md border border-red-500/20">
                    <strong>Falha na Conexão:</strong> Não foi possível conectar ao servidor. Verifique se o backend está no ar e se a porta <strong>3002</strong> não está bloqueada por um firewall.
                </div>
            )}
        </div>

        <form className="bg-white dark:bg-dark-secondary p-8 rounded-lg shadow-xl" onSubmit={handleSubmit}>
          <fieldset disabled={isFormDisabled} className={`space-y-6 transition-opacity duration-300 ${isFormDisabled ? 'opacity-50' : ''}`}>
              <Input
                id="email"
                label="E-mail"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="adm@teste.com"
              />
              <Input
                id="password"
                label="Senha"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="123"
              />
          </fieldset>
          
          {error && <p className="text-center text-sm text-red-500 mt-4">{error}</p>}

          <div className="mt-6">
            <Button type="submit" className="w-full" isLoading={isLoading} disabled={isFormDisabled}>
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};